import React from 'react';
import { Route, Switch } from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import { ToastContext, useMixpanel } from '@weezevent/nacre';
import { useAuth } from 'react-oidc-context';

import Loading from './components/loading';
import Template from './components/template/template';

import { Login } from './pages/login/login';

import { fetcher } from '@weezevent/weezjs-core';

import AuthRoute from './utils/authRoute';
import PageError from './pages/error/pageError';
import { useTrackingPageAuto } from './components/tracking/trackingProvider';
import { usePageTitleUpdater } from './components/utils/usePageTitleUpdater';
import { SmartTableConfigProvider } from '@weezevent/smart-table';

// Lazy load heavy routers and pages
const OrganizationRouter = React.lazy(() => import('./components/routing/organizationRouter'));
const MinisiteRouter = React.lazy(() => import('./components/routing/minisiteRouter'));
const Queue = React.lazy(() => import('./pages/queue/queueChart'));
const ChfSepa = React.lazy(() => import('./pages/tools/chfSepa'));
const ServicesReport = React.lazy(() => import('./pages/tools/servicesReport'));

export default function App() {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const { user } = useAuth();

    React.useEffect(() => {
        const handle_403 = ({ text, url }) => {
            let expired_token = /expired/;
            if (expired_token.test(text)) {
                // Force relogin
                return window.location.reload();
            }

            // Ignore 403 errors for sign_token endpoint (printer functionality is optional)
            if (url && url.includes('/sign_token')) {
                return;
            }

            toast.error(t('ticket.global.not_authorized'));
        };

        fetcher.on('403', handle_403);
    }, []);

    useMixpanel(mixpanel => {
        mixpanel.identify(user.profile.sub);
        mixpanel.register({
            'Accounts ID': user.profile.sub
        });
    }, []);

    useTrackingPageAuto();
    usePageTitleUpdater();

    const { i18n } = useTranslation();

    return (
        <React.Suspense fallback={<Loading />}>
            <SmartTableConfigProvider locale={i18n.language}>
                <div className="app-content">
                    <Switch>
                        {/* Redirect to orga if we ended up in root */}
                        <Route exact path="/ticket">
                            <Login />
                        </Route>
                        {/* for minisite only , allow capacity 'GeminoCapacity.OWNER'*/}
                        <Route
                            path="/ticket/minisite/O:organizationId(\d+)/E:eventId(\d+)"
                            render={basenameProps => {
                                let { organizationId, eventId } = basenameProps.match.params;
                                return <MinisiteRouter organizationId={organizationId} eventId={eventId} history={basenameProps.history} match={basenameProps.match} />;
                            }}
                        />
                        {/* organization-route */}
                        <Route
                            path="/ticket/O:organizationId(\d+)"
                            render={basenameProps => {
                                let { organizationId } = basenameProps.match.params;
                                return <OrganizationRouter organizationId={organizationId} history={basenameProps.history} match={basenameProps.match} />;
                            }}
                        />
                        {/* queues-route */}
                        <Route
                            exact
                            path="/ticket/queues/stats"
                            render={() => (
                                <Template>
                                    <Queue displayHeader={true} />
                                </Template>
                            )}
                        />
                        {/* tools */}
                        <AuthRoute
                            exact
                            path="/ticket/tools/chf-tool"
                            render={() => (
                                <Template>
                                    <ChfSepa />
                                </Template>
                            )}
                        />
                        <AuthRoute
                            exact
                            path="/ticket/tools/services-report"
                            render={() => (
                                <Template>
                                    <ServicesReport />
                                </Template>
                            )}
                        />
                        <Route
                            render={() => (
                                <Template>
                                    <PageError errorCode={404} backHomeUrl={'/ticket'} />
                                </Template>
                            )}
                        />
                    </Switch>
                </div>
            </SmartTableConfigProvider>
        </React.Suspense>
    );
}

App.propTypes = {};
